import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { CheckCircle2 } from '@/components/icons';
import { apiError } from '@/lib/apiError';
import { api } from '@/api/client';
import { Field } from './ChangePasswordModal';

/**
 * Login screen "Forgot password?" — emails a reset link (POST /auth/forgot-password).
 * The server answers the same way whether or not the account exists, so the
 * confirmation is worded as "if…".
 */
export function ForgotPasswordModal({ open, onClose, defaultIdentifier = '' }: {
  open: boolean; onClose: () => void; defaultIdentifier?: string;
}) {
  const [identifier, setIdentifier] = useState(defaultIdentifier);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // On open, prefill whatever was typed on the login screen.
  useEffect(() => { if (open) { setIdentifier(defaultIdentifier); setSent(false); setErr(null); } }, [open, defaultIdentifier]);

  async function submit() {
    if (!identifier.trim()) return;
    setBusy(true); setErr(null);
    try {
      await api.post('/auth/forgot-password', { identifier: identifier.trim() });
      setSent(true);
    } catch (e) {
      setErr(apiError(e, 'Could not send the reset link — try again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title="Forgot password"
      footer={
        <div className="flex w-full justify-end gap-2">
          {sent ? (
            <Button variant="primary" onClick={onClose}>Back to sign in</Button>
          ) : (
            <>
              <Button variant="glass" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button variant="primary" onClick={submit} disabled={busy || !identifier.trim()}>
                {busy ? 'Sending…' : 'Send reset link'}
              </Button>
            </>
          )}
        </div>
      }
    >
      {sent ? (
        <div className="flex gap-3 text-sm text-[var(--text-secondary)]">
          <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-success" />
          <div className="space-y-2">
            <p>If <b className="text-[var(--text-primary)]">{identifier.trim()}</b> matches an active account, a reset link is on its way to the email address on that account.</p>
            <p className="text-xs text-[var(--text-muted)]">The link works once and expires in 30 minutes. No email? Check spam, or ask an admin to reset your password from User Management.</p>
          </div>
        </div>
      ) : (
        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
          <p className="text-sm text-[var(--text-secondary)]">Enter your username or email. We&apos;ll email you a link to set a new password.</p>
          <Field label="Username or email" value={identifier} onChange={setIdentifier} type="text" autoComplete="username" />
          {err && <p className="text-xs text-danger">{err}</p>}
        </form>
      )}
    </Modal>
  );
}
